import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Facebook, Instagram, Youtube, Mail, Phone, MapPin, ArrowUp } from 'lucide-react';
import { SiTelegram, SiX } from 'react-icons/si';
import { useAppSelector } from '@/app/hooks';
import { cn } from '@/lib/utils';
import { useGetHomeSlidersQuery } from '@/services/api';
import DOMPurify from 'dompurify';

export function Footer() {
  const { t } = useTranslation();
  const dir = useAppSelector((s) => s.i18n.dir);
  const currentLang = useAppSelector((state) => state.i18n.language);
  const currentYear = new Date().getFullYear();
  const [showUp, setShowUp] = useState(false);
  
  // Get footer info from home slider API
  const { data: homeSlidersData } = useGetHomeSlidersQuery({ lang: currentLang });
  const footerInfo = homeSlidersData?.footer_info;

  // Get bilingual Iraq name based on current language (same as header)
  const getIraqName = () => {
    if (currentLang === 'ar') {
      return 'جمهورية العراق | کۆماری عێراق';
    } else if (currentLang === 'en') {
      return 'Republic of Iraq | کۆماری عێراق';
    } else {
      return 'کۆماری عێراق | جمهورية العراق';
    }
  };

  useEffect(() => {
    let ticking = false;
    const update = () => {
      setShowUp(window.scrollY > 400);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/Moys.iraq/?locale=ar_AR', label: 'Facebook', color: 'hover:text-blue-500' },
    { icon: Instagram, href: 'https://www.instagram.com/moys.iq/?hl=ar', label: 'Instagram', color: 'hover:text-pink-500' },
    { icon: SiTelegram, href: 'https://t.me/s/Moys_iraq', label: 'Telegram', color: 'hover:text-blue-400' },
    { icon: SiX, href: 'https://x.com/moys_iraq?lang=ar', label: 'X (Twitter)', color: 'hover:text-gray-300' },
    { icon: Youtube, href: 'https://www.youtube.com/channel/UCOkiIsGqiprsbyxjFahg5Hg/videos?view=0&sort=da', label: 'YouTube', color: 'hover:text-red-500' },
  ];

  // Focus on the most important links in the footer to reduce clutter
  const quickLinks = [
    { name: t('nav.ministry.about'), href: '/about' },
    { name: t('nav.ministry.departments'), href: '/departments' },
    { name: t('nav.info.tenders'), href: '/tenders' },
    { name: t('nav.info.events'), href: '/events' },
  ];

  const services = [
    { name: t('nav.services.oor'), href: '/e-services/oor' },
    { name: t('nav.services.forms'), href: '/e-forms' },
    { name: t('nav.services.complaints'), href: '/complaints' },
    { name: t('nav.services.tests'), href: '/tests' },
  ];

  return (
    <footer 
      className="bg-gradient-to-br from-[#085210] via-[#0a6e15] to-[#0d8a1a] text-white"
      role="contentinfo"
      aria-label={t('footer.title', { defaultValue: 'تذييل الصفحة' })}
    >
      {/* Main Footer */}
      <div className="container-elegant py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* About Section */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 bg-white rounded-full p-1 shadow-soft-lg">
                <img 
                  src="/Icons/logo.png" 
                  alt="شعار وزارة الشباب والرياضة"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h3 className="font-bold text-lg">{t('common.ministry')}</h3>
                <p className="text-sm text-white/70">{getIraqName()}</p>
              </div>
            </div>
            {/* Description with HTML rendering */}
            {footerInfo?.description ? (
              <div 
                className="text-sm text-white/80 leading-relaxed mb-6 prose prose-sm prose-invert max-w-none prose-p:text-white/80 prose-p:my-0"
                dangerouslySetInnerHTML={{ 
                  __html: DOMPurify.sanitize(footerInfo.description) 
                }}
              />
            ) : (
              <p className="text-sm text-white/80 leading-relaxed mb-6">
                {t('about.visionText')}
              </p>
            )}
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 transition-all ${social.color} hover:bg-white hover:scale-110 hover:shadow-lg`}
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6 relative inline-block">
              {t('footer.quickLinks', { defaultValue: 'روابط سريعة' })}
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-[#F4B400]"></span>
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-white/80 hover:text-white hover:translate-x-1 inline-flex items-center gap-2 transition-all"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F4B400]"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* E-Services */}
          <div>
            <h4 className="text-lg font-bold mb-6 relative inline-block">
              {t('nav.services.title')}
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-[#F4B400]"></span>
            </h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.href}>
                  <Link
                    to={service.href}
                    className="text-sm text-white/80 hover:text-white hover:translate-x-1 inline-flex items-center gap-2 transition-all"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F4B400]"></span>
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Government Links (from translations) */}
          <div>
            {/** Use i18n objects for labels and URLs so translators can update them */}
            {(() => {
              const government = t('footer.government', { returnObjects: true }) as any;
              const links = government?.links || [];
              const title = government?.title || t('footer.quickLinks');
              return (
                <>
                  <h4 className="text-lg font-bold mb-6 relative inline-block">{title}<span className="absolute -bottom-2 left-0 w-12 h-1 bg-[#F4B400]"></span></h4>
                  <ul className="space-y-3">
                    {links.map((link: any) => (
                      <li key={`${link.label}-${link.url}`}>
                        <a
                          href={link.url}
                          target={link.url && link.url !== '#' ? '_blank' : undefined}
                          rel={link.url && link.url !== '#' ? 'noopener noreferrer' : undefined}
                          className="text-sm text-white/80 hover:text-white hover:translate-x-1 inline-flex items-center gap-2 transition-all"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-[#F4B400]"></span>
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()}
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-6 relative inline-block">
              {t('footer.contact')}
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-[#F4B400]"></span>
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-white/80">
                <MapPin className="w-5 h-5 flex-shrink-0 text-[#F4B400] mt-0.5" />
                <span>{footerInfo?.address || t('footer.address')}</span>
              </li>
              {footerInfo?.phone && (
                <li className="flex items-center gap-3 text-sm text-white/80 hover:text-white transition-colors">
                  <Phone className="w-5 h-5 flex-shrink-0 text-[#F4B400]" />
                  <a href={`tel:${footerInfo.phone}`} dir="ltr">{footerInfo.phone}</a>
                </li>
              )}
              {footerInfo?.email && (
                <li className="flex items-center gap-3 text-sm text-white/80 hover:text-white transition-colors">
                  <Mail className="w-5 h-5 flex-shrink-0 text-[#F4B400]" />
                  <a href={`mailto:${footerInfo.email}`}>{footerInfo.email}</a>
                </li>
              )}
            </ul>
          
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 bg-gradient-to-r from-[#0a6e15]/60 to-[#085210]/60">
        <div className="container-elegant py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col md:flex-row items-center gap-4 text-sm text-white/80 text-center md:text-left">
              <div>{t('footer.copyright', { year: currentYear })}</div>
              <div className="hidden md:block text-white/40">|</div>
              <a
                href="https://www.borderlesssecurity.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/60 hover:text-white/90 transition-colors inline-flex items-center gap-1"
              >
                {t('footer.designedBy')}
              </a>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/privacy" className="text-white/80 hover:text-white transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to="/terms" className="text-white/80 hover:text-white transition-colors">
                {t('footer.terms')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      {showUp && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className={cn(
            'fixed rounded-full bg-primary hover:bg-primary/90 text-white shadow-soft-lg',
            'flex items-center justify-center transition-all hover:scale-110',
            'z-[2000] border-2 border-white/10',
            'w-12 h-12 md:w-14 md:h-14', // Smaller on mobile
            'bottom-4 md:bottom-6', // Same level as accessibility button
            // Position on opposite side from accessibility button
            dir === 'rtl' ? 'left-4 md:left-6' : 'right-4 md:right-6'
          )}
          aria-label={t('footer.backToTop', { defaultValue: 'العودة للأعلى' })}
        >
          <ArrowUp className="w-6 h-6 md:w-7 md:h-7" />
        </button>
      )}
    </footer>
  );
}
